import React, { useEffect, useState } from 'react'
import useCsrfToken from '../../hooks/useCsrfToken'

interface Store {
  id: number
  name: string
  api_provider: string
  external_store_id: string
  active: boolean
  store_products_count: number
  last_synced_at: string | null
}

const AdminStores: React.FC = () => {
  const csrfToken = useCsrfToken()
  const [stores, setStores] = useState<Store[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [syncStatus, setSyncStatus] = useState<Record<number, string>>({})

  const [newName, setNewName] = useState('')
  const [newLocationId, setNewLocationId] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const loadStores = () => {
    fetch('/api/admin/stores', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => { setStores(data.stores); setLoading(false) })
      .catch(() => { setError('Failed to load stores'); setLoading(false) })
  }

  useEffect(() => { loadStores() }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreating(true)
    setCreateError(null)
    const res = await fetch('/api/admin/stores', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        name: newName,
        api_provider: 'kroger',
        external_store_id: newLocationId,
      }),
    })
    const data = await res.json()
    setCreating(false)
    if (res.ok) {
      setNewName('')
      setNewLocationId('')
      loadStores()
    } else {
      setCreateError(data.errors?.join(', ') || data.error || 'Failed to create store')
    }
  }

  const handleToggleActive = async (store: Store) => {
    await fetch(`/api/admin/stores/${store.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ active: !store.active }),
    })
    loadStores()
  }

  const handleDelete = async (store: Store) => {
    if (!confirm(`Delete ${store.name}? All products and price history will be removed.`)) return
    await fetch(`/api/admin/stores/${store.id}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
    })
    setStores((prev) => prev.filter((s) => s.id !== store.id))
  }

  const handleSync = async (store: Store) => {
    setSyncStatus((prev) => ({ ...prev, [store.id]: 'Queuing…' }))
    const res = await fetch(`/api/admin/stores/${store.id}/sync`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
    })
    if (res.ok) {
      setSyncStatus((prev) => ({ ...prev, [store.id]: 'Queued!' }))
      setTimeout(() => setSyncStatus((prev) => ({ ...prev, [store.id]: '' })), 3000)
    } else {
      setSyncStatus((prev) => ({ ...prev, [store.id]: 'Error' }))
    }
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return 'Never'
    return new Date(iso).toLocaleString()
  }

  return (
    <div className="space-y-6">
      <div className="bg-navy-900 border border-army-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-army-800">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Stores</p>
        </div>
        {loading ? (
          <p className="px-4 py-6 text-gray-500 text-sm">Loading…</p>
        ) : error ? (
          <p className="px-4 py-6 text-rose-400 text-sm">{error}</p>
        ) : stores.length === 0 ? (
          <p className="px-4 py-6 text-gray-500 text-sm">No stores configured yet.</p>
        ) : (
          <ul className="divide-y divide-army-800">
            {stores.map((store) => (
              <li key={store.id} className="px-4 py-4 space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-stone-200">{store.name}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-army-800 text-gray-400 font-mono">
                        {store.api_provider}
                      </span>
                      {!store.active && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-rose-900 text-rose-300">
                          inactive
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Location ID: <span className="font-mono text-gray-400">{store.external_store_id}</span>
                      {' · '}{store.store_products_count.toLocaleString()} products
                      {' · '}Last synced: {formatDate(store.last_synced_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleSync(store)}
                      disabled={!!syncStatus[store.id]}
                      className="text-xs px-2.5 py-1 rounded bg-army-800 text-neon hover:bg-army-700 transition-colors disabled:opacity-60"
                    >
                      {syncStatus[store.id] || 'Sync Now'}
                    </button>
                    <button
                      onClick={() => handleToggleActive(store)}
                      className="text-xs text-gray-500 hover:text-stone-300 transition-colors"
                    >
                      {store.active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => handleDelete(store)}
                      className="text-xs text-gray-600 hover:text-rose-400 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-navy-900 border border-army-800 rounded-lg p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Add King Soopers / Kroger store</p>
        <form onSubmit={handleCreate} className="space-y-3">
          <input
            type="text"
            required
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Store name (e.g. King Soopers — Havana)"
            className="w-full px-3 py-2 bg-army-950 border border-army-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon transition-colors text-sm"
          />
          <input
            type="text"
            required
            value={newLocationId}
            onChange={(e) => setNewLocationId(e.target.value)}
            placeholder="Kroger location ID (find via /v1/locations API)"
            className="w-full px-3 py-2 bg-army-950 border border-army-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon transition-colors text-sm font-mono"
          />
          {createError && <p className="text-sm text-rose-400">{createError}</p>}
          <button
            type="submit"
            disabled={creating}
            className="px-4 py-2 bg-neon text-army-950 font-semibold rounded-lg text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {creating ? 'Adding…' : 'Add store'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default AdminStores
